/**
 * Abstract floorplan editor for reunification plan templates.
 *
 * Composition root: floorplan tabs + settings header, tool palette (left),
 * SVG canvas (center), properties panel (right). The canvas works on a
 * local draft with undo/redo; Save diff-syncs against the nested
 * floorplan/station/zone/flow routers (see syncFloorplanDraft.ts).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Center,
  Field,
  Flex,
  HStack,
  IconButton,
  Spinner,
  Switch,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { Button as ButtonComponent, InputField, Tooltip } from '@themes/aegix';
import { HiCog6Tooth, HiPlus, HiTrash } from 'react-icons/hi2';
import {
  useCreatePlanFloorplan,
  useDeletePlanFloorplan,
  useInvalidatePlanFloorplans,
  usePlanFloorplan,
  usePlanFloorplans,
  useUpdatePlanFloorplan,
} from '../../../hooks/queries/useReunification';
import { toaster } from '../../ui/toaster';
import { EditorCanvas } from './EditorCanvas';
import { EditorToolbar } from './EditorToolbar';
import { PropertiesPanel } from './PropertiesPanel';
import { syncFloorplanDraft } from './syncFloorplanDraft';
import { useFloorplanDraft } from './useFloorplanDraft';
import type { EditorTool, FlowEndpoint, Selection } from './types';

interface FloorplanEditorProps {
  templateId: string;
}

function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return (
    !!el &&
    (el.tagName === 'INPUT' ||
      el.tagName === 'TEXTAREA' ||
      el.isContentEditable)
  );
}

export function FloorplanEditor({ templateId }: FloorplanEditorProps) {
  const { t } = useTranslation();
  const { data: floorplans, isLoading: listLoading } =
    usePlanFloorplans(templateId);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [tool, setTool] = useState<EditorTool>({ kind: 'select' });
  const [selection, setSelection] = useState<Selection | null>(null);
  const [flowSource, setFlowSource] = useState<FlowEndpoint | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const createFloorplan = useCreatePlanFloorplan(templateId);
  const updateFloorplan = useUpdatePlanFloorplan(templateId);
  const deleteFloorplan = useDeletePlanFloorplan(templateId);
  const invalidateFloorplans = useInvalidatePlanFloorplans(templateId);

  // Default to the first floorplan once the list arrives.
  useEffect(() => {
    if (!activeId && floorplans?.length) {
      setActiveId(floorplans[0].public_id);
    }
    if (
      activeId &&
      floorplans &&
      !floorplans.some((f) => f.public_id === activeId)
    ) {
      setActiveId(floorplans[0]?.public_id ?? null);
    }
  }, [floorplans, activeId]);

  const { data: activeFloorplan, isLoading: detailLoading } = usePlanFloorplan(
    templateId,
    activeId,
  );

  const { draft, baseline, canUndo, canRedo, isDirty, load, dispatch } =
    useFloorplanDraft();

  // (Re)load the draft whenever a fresh server copy of the active floorplan
  // arrives. Guard on dirty (via ref, so mere dirty-flips don't re-run the
  // effect and reset undo history) so background refetches can't clobber edits.
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  useEffect(() => {
    if (!activeFloorplan) return;
    if (activeFloorplan.public_id !== loadedId || !isDirtyRef.current) {
      load(activeFloorplan);
      setLoadedId(activeFloorplan.public_id);
      setSelection(null);
      setFlowSource(null);
    }
  }, [activeFloorplan, loadedId, load]);

  const saveMutation = useMutation({
    mutationFn: () =>
      syncFloorplanDraft({
        templateId,
        floorplanId: activeId as string,
        baseline,
        draft,
      }),
    onSuccess: (writes) => {
      toaster.create({
        title: t(
          'reunification.floorplanSaved',
          'Floorplan saved ({{count}} changes)',
          { count: writes },
        ),
        type: 'success',
      });
      setLoadedId(null); // force draft reload from the refetched server copy
      invalidateFloorplans();
    },
    onError: (err) => {
      toaster.create({
        title: t(
          'reunification.failedSaveFloorplan',
          'Failed to save floorplan',
        ),
        description: err instanceof Error ? err.message : undefined,
        type: 'error',
      });
      // Partial writes may have landed; refetch so the next save diffs
      // against true server state instead of double-applying.
      setLoadedId(null);
      invalidateFloorplans();
    },
  });

  const handleUndo = useCallback(() => dispatch({ type: 'undo' }), [dispatch]);
  const handleRedo = useCallback(() => dispatch({ type: 'redo' }), [dispatch]);

  // Keyboard: undo/redo + delete-selection (suppressed while typing).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) handleRedo();
        else handleUndo();
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selection) {
        e.preventDefault();
        if (selection.kind === 'station')
          dispatch({ type: 'remove_station', cid: selection.cid });
        else if (selection.kind === 'zone')
          dispatch({ type: 'remove_zone', cid: selection.cid });
        else dispatch({ type: 'remove_flow', cid: selection.cid });
        setSelection(null);
      }
      if (e.key === 'Escape') {
        setFlowSource(null);
        setTool({ kind: 'select' });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selection, dispatch, handleUndo, handleRedo]);

  const handleTabChange = (floorplanId: string) => {
    if (isDirty) {
      toaster.create({
        title: t('reunification.unsavedChanges', 'Unsaved changes'),
        description: t(
          'reunification.unsavedChangesDescription',
          'Save or discard your changes before switching floorplans.',
        ),
        type: 'warning',
      });
      return;
    }
    setActiveId(floorplanId);
  };

  const handleAddFloorplan = () => {
    const name = t(
      'reunification.floorplanDefaultName',
      'Floorplan {{number}}',
      {
        number: (floorplans?.length ?? 0) + 1,
      },
    );
    createFloorplan.mutate(
      { name, is_primary: (floorplans?.length ?? 0) === 0 },
      {
        onSuccess: (created) => setActiveId(created.public_id),
        onError: () =>
          toaster.create({
            title: t(
              'reunification.failedAddFloorplan',
              'Failed to add floorplan',
            ),
            type: 'error',
          }),
      },
    );
  };

  const handleDeleteFloorplan = () => {
    if (!activeId) return;
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    setConfirmingDelete(false);
    deleteFloorplan.mutate(activeId, {
      onSuccess: () => {
        toaster.create({
          title: t('reunification.floorplanDeleted', 'Floorplan deleted'),
          type: 'success',
        });
        setActiveId(null);
        setLoadedId(null);
      },
      onError: () =>
        toaster.create({
          title: t(
            'reunification.failedDeleteFloorplan',
            'Failed to delete floorplan',
          ),
          type: 'error',
        }),
    });
  };

  if (listLoading) {
    return (
      <Center h="full" minH="20rem">
        <Spinner size="lg" color="button.primary" />
      </Center>
    );
  }

  if (!floorplans?.length) {
    return (
      <Center h="full" minH="20rem">
        <Box textAlign="center">
          <Text color="text.secondary" mb={4}>
            {t(
              'reunification.noFloorplansYet',
              'No floorplans yet. Create one to start designing the reunification layout.',
            )}
          </Text>
          <ButtonComponent
            visual="primary"
            onClick={handleAddFloorplan}
            disabled={createFloorplan.isPending}
            data-e2e="floorplan-create-first"
          >
            {createFloorplan.isPending
              ? t('reunification.creating', 'Creating…')
              : t('reunification.createFloorplan', 'Create Floorplan')}
          </ButtonComponent>
        </Box>
      </Center>
    );
  }

  return (
    <Flex direction="column" h="full" minH={0} data-e2e="floorplan-editor">
      {/* Header: floorplan tabs + actions */}
      <HStack
        px={3}
        py={2}
        borderBottomWidth="1px"
        borderColor="border"
        gap={2}
      >
        <Tabs.Root
          value={activeId ?? undefined}
          onValueChange={(d) => handleTabChange(d.value)}
          variant="enclosed"
          size="sm"
        >
          <Tabs.List>
            {floorplans.map((fp) => (
              <Tabs.Trigger
                key={fp.public_id}
                value={fp.public_id}
                data-e2e={`floorplan-tab-${fp.public_id}`}
              >
                {fp.name}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
        </Tabs.Root>
        <Tooltip content={t('reunification.addFloorplan', 'Add floorplan')}>
          <IconButton
            aria-label={t('reunification.addFloorplan', 'Add floorplan')}
            size="sm"
            variant="ghost"
            onClick={handleAddFloorplan}
            loading={createFloorplan.isPending}
            data-e2e="floorplan-add"
          >
            <HiPlus />
          </IconButton>
        </Tooltip>
        <Tooltip
          content={t('reunification.floorplanSettings', 'Floorplan settings')}
        >
          <IconButton
            aria-label={t(
              'reunification.floorplanSettings',
              'Floorplan settings',
            )}
            size="sm"
            variant={settingsOpen ? 'solid' : 'ghost'}
            onClick={() => setSettingsOpen((open) => !open)}
            data-e2e="floorplan-settings-toggle"
          >
            <HiCog6Tooth />
          </IconButton>
        </Tooltip>
        <Tooltip
          content={
            confirmingDelete
              ? t(
                  'reunification.confirmDeleteClick',
                  'Click again to confirm delete',
                )
              : t('reunification.deleteFloorplan', 'Delete floorplan')
          }
        >
          <IconButton
            aria-label={t('reunification.deleteFloorplan', 'Delete floorplan')}
            size="sm"
            variant="ghost"
            colorPalette={confirmingDelete ? 'red' : 'gray'}
            onClick={handleDeleteFloorplan}
            onBlur={() => setConfirmingDelete(false)}
            loading={deleteFloorplan.isPending}
            data-e2e="floorplan-delete"
          >
            <HiTrash />
          </IconButton>
        </Tooltip>
        <Box flex="1" />
        {isDirty && (
          <Text
            fontSize="xs"
            color="text.warning"
            data-e2e="floorplan-dirty-indicator"
          >
            {t('reunification.unsavedChanges', 'Unsaved changes')}
          </Text>
        )}
        <ButtonComponent
          visual="secondary"
          size="sm"
          disabled={!isDirty || saveMutation.isPending}
          onClick={() => {
            setLoadedId(null);
            invalidateFloorplans();
          }}
          data-e2e="floorplan-discard"
        >
          {t('reunification.discard', 'Discard')}
        </ButtonComponent>
        <ButtonComponent
          visual="primary"
          size="sm"
          disabled={!isDirty || saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
          data-e2e="floorplan-save"
        >
          {saveMutation.isPending
            ? t('reunification.saving', 'Saving…')
            : t('reunification.saveFloorplan', 'Save Floorplan')}
        </ButtonComponent>
      </HStack>

      {/* Collapsible floorplan settings */}
      {settingsOpen && activeFloorplan && (
        <HStack
          px={3}
          py={2}
          gap={3}
          borderBottomWidth="1px"
          borderColor="border"
          align="end"
          flexWrap="wrap"
        >
          <Field.Root maxW="14rem">
            <Field.Label>{t('common.name', 'Name')}</Field.Label>
            <InputField
              size="sm"
              key={`fp-name-${activeFloorplan.public_id}-${activeFloorplan.name}`}
              defaultValue={activeFloorplan.name}
              onBlur={(e) => {
                if (e.target.value && e.target.value !== activeFloorplan.name) {
                  updateFloorplan.mutate({
                    id: activeFloorplan.public_id,
                    data: { name: e.target.value },
                  });
                }
              }}
              data-e2e="floorplan-settings-name"
            />
          </Field.Root>
          <Field.Root maxW="8rem">
            <Field.Label>
              {t('reunification.canvasWidth', 'Canvas width')}
            </Field.Label>
            <InputField
              size="sm"
              type="number"
              min={200}
              key={`fp-w-${activeFloorplan.public_id}-${activeFloorplan.canvas_width}`}
              defaultValue={activeFloorplan.canvas_width}
              onBlur={(e) => {
                const width = parseInt(e.target.value, 10);
                if (
                  !Number.isNaN(width) &&
                  width >= 200 &&
                  width !== activeFloorplan.canvas_width
                ) {
                  updateFloorplan.mutate({
                    id: activeFloorplan.public_id,
                    data: { canvas_width: width },
                  });
                }
              }}
              data-e2e="floorplan-settings-width"
            />
          </Field.Root>
          <Field.Root maxW="8rem">
            <Field.Label>
              {t('reunification.canvasHeight', 'Canvas height')}
            </Field.Label>
            <InputField
              size="sm"
              type="number"
              min={200}
              key={`fp-h-${activeFloorplan.public_id}-${activeFloorplan.canvas_height}`}
              defaultValue={activeFloorplan.canvas_height}
              onBlur={(e) => {
                const height = parseInt(e.target.value, 10);
                if (
                  !Number.isNaN(height) &&
                  height >= 200 &&
                  height !== activeFloorplan.canvas_height
                ) {
                  updateFloorplan.mutate({
                    id: activeFloorplan.public_id,
                    data: { canvas_height: height },
                  });
                }
              }}
              data-e2e="floorplan-settings-height"
            />
          </Field.Root>
          <Field.Root maxW="22rem" flex="1">
            <Field.Label>
              {t(
                'reunification.backgroundImageUrl',
                'Background image URL (conceptual diagram)',
              )}
            </Field.Label>
            <InputField
              size="sm"
              key={`fp-bg-${activeFloorplan.public_id}-${activeFloorplan.diagram_url}`}
              defaultValue={activeFloorplan.diagram_url}
              placeholder="https://…"
              onBlur={(e) => {
                if (e.target.value !== activeFloorplan.diagram_url) {
                  updateFloorplan.mutate({
                    id: activeFloorplan.public_id,
                    data: { diagram_url: e.target.value },
                  });
                }
              }}
              data-e2e="floorplan-settings-diagram-url"
            />
          </Field.Root>
          <Switch.Root
            size="sm"
            checked={activeFloorplan.is_primary}
            onCheckedChange={(d) =>
              updateFloorplan.mutate({
                id: activeFloorplan.public_id,
                data: { is_primary: !!d.checked },
              })
            }
            data-e2e="floorplan-settings-primary"
          >
            <Switch.HiddenInput />
            <Switch.Control />
            <Switch.Label>{t('reunification.primary', 'Primary')}</Switch.Label>
          </Switch.Root>
        </HStack>
      )}

      {/* Body: palette | canvas | properties */}
      <Flex flex="1" minH={0}>
        <EditorToolbar
          tool={tool}
          canUndo={canUndo}
          canRedo={canRedo}
          onToolChange={(next) => {
            setTool(next);
            setFlowSource(null);
          }}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onClear={() => {
            dispatch({ type: 'clear' });
            setSelection(null);
            setFlowSource(null);
          }}
        />
        {detailLoading || !activeFloorplan ? (
          <Center flex="1">
            <Spinner size="lg" color="button.primary" />
          </Center>
        ) : (
          <EditorCanvas
            draft={draft}
            tool={tool}
            selection={selection}
            canvasWidth={activeFloorplan.canvas_width}
            canvasHeight={activeFloorplan.canvas_height}
            diagramUrl={activeFloorplan.diagram_url}
            flowSource={flowSource}
            onSelect={setSelection}
            onFlowSource={setFlowSource}
            dispatch={dispatch}
          />
        )}
        <PropertiesPanel
          draft={draft}
          selection={selection}
          dispatch={dispatch}
          onDeselect={() => setSelection(null)}
        />
      </Flex>

      {tool.kind === 'draw-flow' && (
        <Box
          px={3}
          py={1}
          bg="bg.subtle"
          borderTopWidth="1px"
          borderColor="border"
        >
          <Text fontSize="xs" color="text.secondary">
            {flowSource
              ? t(
                  'reunification.flowClickTarget',
                  'Now click the target station or zone to draw the flow.',
                )
              : t(
                  'reunification.flowClickSource',
                  'Click the source station or zone for this flow.',
                )}
          </Text>
        </Box>
      )}
    </Flex>
  );
}
